import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { User, Loader2 } from 'lucide-react';
import { PaywallModal } from '@/components/paywall/paywall-modal';
import { useHistoryStore } from '@/lib/store/history-store';
import { initiateDeepSearch } from '@/lib/api/deep-search';
import { logAnalyticsEvent } from '@/lib/firebase/analytics';

export default function SearchPersonPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [showPaywall, setShowPaywall] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isSubscribed, isSubscriptionLoaded, loadSubscriptionFromFirestore, userId, addSearch } = useHistoryStore();

  useEffect(() => {
    document.title = 'Person Search - Deepcheck';
  }, []);

  // Load subscription status on mount
  useEffect(() => {
    if (!isSubscriptionLoaded) {
      loadSubscriptionFromFirestore();
    }
  }, [isSubscriptionLoaded, loadSubscriptionFromFirestore]);

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) {
      setQuery(q);
    }
  }, [searchParams]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;

    setError(null);

    // Check subscription status from Firestore state
    if (!isSubscribed) {
      setShowPaywall(true);
      return;
    }

    setIsSubmitting(true);

    try {
      // Call the real deep search API (cloud function verifies subscription)
      const result = await initiateDeepSearch(userId, query, 'person-search');

      logAnalyticsEvent('search_start', { type: 'person' });

      // Add to local store with queued status
      addSearch({
        id: result.searchId,
        type: 'person-search',
        query: query,
        status: 'queued',
        progress: 0,
        timestamp: new Date(),
      });

      // Navigate to analyzing page (listener will handle real-time updates)
      navigate(`/analyzing?id=${result.searchId}&type=person-search&query=${encodeURIComponent(query)}`);
    } catch (error) {
      console.error('Failed to start search:', error);
      setError(error instanceof Error ? error.message : 'Failed to start search');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubscribe = () => {
    setShowPaywall(false);
    // After subscription, user can try searching again
  };

  const handleClosePaywall = () => {
    setShowPaywall(false);
  };

  return (
    <main>
      <div className="flex flex-col items-center justify-center min-h-[80vh] px-4">
        <div className="w-full max-w-md space-y-8 text-center">
          {/* Icon */}
          <div className="flex justify-center">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
              <User className="w-10 h-10 text-green-600" />
            </div>
          </div>

          {/* Title */}
          <div className="space-y-3">
            <h1 className="text-3xl font-bold text-gray-900">Person Search</h1>
            <p className="text-gray-600">
              Search for a person&apos;s online presence
            </p>
          </div>

          {/* Search Form */}
          <form onSubmit={handleSearch} className="space-y-4">
            <Input
              type="text"
              placeholder="Search for someone..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-12 text-base"
              autoFocus
            />
            {error && (
              <p className="text-sm text-red-600 text-center">{error}</p>
            )}
            <Button
              type="submit"
              className="w-full h-12 bg-black hover:bg-gray-800 text-white font-semibold"
              disabled={!query.trim() || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Starting...
                </>
              ) : (
                'Search Now'
              )}
            </Button>
          </form>
        </div>

        {/* Paywall Modal */}
        <PaywallModal
          open={showPaywall}
          onClose={handleClosePaywall}
          onSubscribe={handleSubscribe}
        />
      </div>
    </main>
  );
}
